/**
 * Owner: Masa Bayu
 * File: publication-metadata.ts
 * Date:Time: 2026-09-15 20:19
 * Description: T1 catalog-safe publication metadata projection (≠ QXP bytes).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertOptionalTrimmed, assertValidId } from './guards';
/** Catalog-safe fields only — never content URLs or QXP payloads. */
export const QXREAD_METADATA_FORBIDDEN = [
    'qxpBytes',
    'qxpPayload',
    'contentUrl',
    'downloadUrl',
    'flipEngine',
    'readerRuntime',
    'entitlementGrant',
];
export function createQxreadPublicationMetadata(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead publication metadata');
    for (const key of QXREAD_METADATA_FORBIDDEN) {
        if (Object.prototype.hasOwnProperty.call(options, key)) {
            throw new Error(`QXRead metadata must not include forbidden field "${key}"`);
        }
    }
    assertValidId(options.publicationId, 'QXRead publicationId');
    const title = assertOptionalTrimmed(options.title, 'QXRead metadata title');
    if (title === null) {
        throw new Error('QXRead metadata title is required');
    }
    const authors = Object.freeze((options.authors ?? []).map((author, index) => {
        const value = assertOptionalTrimmed(author, `QXRead metadata authors[${String(index)}]`);
        if (value === null) {
            throw new Error('QXRead metadata authors must be non-empty strings');
        }
        return value;
    }));
    const language = assertOptionalTrimmed(options.language ?? 'en', 'QXRead metadata language');
    if (language === null) {
        throw new Error('QXRead metadata language is required');
    }
    let coverAssetRef = null;
    if (options.coverAssetRef !== undefined && options.coverAssetRef !== null) {
        assertValidId(options.coverAssetRef, 'QXRead coverAssetRef');
        coverAssetRef = options.coverAssetRef;
    }
    const tags = Object.freeze((options.tags ?? []).map((tag, index) => {
        const value = assertOptionalTrimmed(tag, `QXRead metadata tags[${String(index)}]`);
        if (value === null) {
            throw new Error('QXRead metadata tags must be non-empty strings');
        }
        return value;
    }));
    return Object.freeze({
        publicationId: options.publicationId,
        title,
        subtitle: assertOptionalTrimmed(options.subtitle, 'QXRead metadata subtitle'),
        authors,
        language,
        description: assertOptionalTrimmed(options.description, 'QXRead metadata description'),
        coverAssetRef,
        tags,
    });
}
/** Serialize catalog-safe metadata for marketplace surfaces (no secrets / content). */
export function serializeCatalogMetadata(metadata) {
    return Object.freeze({
        publicationId: metadata.publicationId,
        title: metadata.title,
        subtitle: metadata.subtitle,
        authors: metadata.authors,
        language: metadata.language,
        description: metadata.description,
        coverAssetRef: metadata.coverAssetRef,
        tags: metadata.tags,
    });
}
//# sourceMappingURL=publication-metadata.js.map